import axios from 'axios';
import { ConnectionState, OAuthInstallParams, PlatformConnectionApi, ShopifyInstallParams, StorefrontApi } from '../../components/platform-setup/types';
import { shopifyAppPath } from '../platform-migration/api';
import { ApiIngestion, Checklist, HelpReason, MigrationChoice, MigrationSource, MigrationState, NotificationsInput, OnboardingContact, OnboardingStorefrontState, OnboardingQueueItem, OnboardingState, OnboardingStepId, OnboardingTeamDetail, TriggerSuggestion, WhatsappLimitCap } from './types';

// O interceptor do axios já põe /:customer no baseURL
const BASE = '/onboarding';
const ADMIN_BASE = () => `${process.env.REACT_APP_API_URL}/konfidency/admin/onboarding`;

// Chamada com o cliente explícito (retornos de OAuth): ignora o cliente ativo do navegador
const explicitCustomerUrl = (customer: string, path: string) =>
  `${process.env.REACT_APP_API_URL}/${encodeURIComponent(customer)}${path}`;

// Retornos de OAuth: o cliente vai no caminho, nunca do cliente ativo (outra aba pode ter trocado)
export const onboardingShopifyCallbackPath = (customer: string) => `/onboarding/callback/shopify/${encodeURIComponent(customer)}`;
export const onboardingOAuthCallbackPath = (platform: string, customer: string, state: string) =>
  `/onboarding/callback/${platform}/${encodeURIComponent(customer)}/${encodeURIComponent(state)}`;

// Nuvemshop: o retorno do app 7412 é fixo (/nuvemshop/callback); cliente e state vão no state
const NUVEMSHOP_STATE_PREFIX = 'ob';
export const nuvemshopOnboardingState = (customer: string, state: string) => [NUVEMSHOP_STATE_PREFIX, customer, state].join('~');

/** { customer, state } quando o state da Nuvemshop é de um onboarding; null nos outros fluxos */
export const parseNuvemshopOnboardingState = (raw: string | null): { customer: string; state: string } | null => {
  const parts = (raw || '').split('~');
  if (parts.length !== 3 || parts[0] !== NUVEMSHOP_STATE_PREFIX || !parts[1] || !/^[a-f0-9]{48}$/.test(parts[2])) return null;
  return { customer: parts[1], state: parts[2] };
};

const oauthAuthorizeUrlFor = (customer: string) => (install: OAuthInstallParams, storeOrigin?: string) => {
  if (install.platform === 'tray') {
    const callback = window.location.origin + onboardingOAuthCallbackPath('tray', customer, install.state);
    return `${storeOrigin}/auth.php?response_type=code&consumer_key=${encodeURIComponent(install.appId)}&callback=${encodeURIComponent(callback)}`;
  }
  return `https://www.nuvemshop.com.br/apps/${encodeURIComponent(install.appId)}/authorize?state=${encodeURIComponent(
    nuvemshopOnboardingState(customer, install.state)
  )}`;
};

export const onboardingApi = {
  async get(): Promise<OnboardingState> {
    return (await axios.get(BASE)).data;
  },
  async setCurrentStep(step: OnboardingStepId): Promise<OnboardingState> {
    return (await axios.put(`${BASE}/current-step`, { step })).data;
  },
  async saveStore(input: { contact: OnboardingContact; name: string; storeUrl: string }): Promise<OnboardingState> {
    return (await axios.put(`${BASE}/steps/store`, input)).data;
  },
  async savePlatform(input: { storefront: string }): Promise<OnboardingState> {
    return (await axios.put(`${BASE}/steps/platform`, input)).data;
  },
  async saveNotifications(input: NotificationsInput): Promise<OnboardingState> {
    return (await axios.put(`${BASE}/steps/notifications`, input)).data;
  },
  async saveMigration(input: { choice: MigrationChoice; source?: MigrationSource; volumes?: Record<string, number>; trayUserConfirmed?: boolean }): Promise<OnboardingState> {
    return (await axios.put(`${BASE}/steps/migration`, input)).data;
  },
  async getMigration(): Promise<MigrationState> {
    return (await axios.get(`${BASE}/migration`)).data;
  },
  async saveTrustvox(input: { storeId: string; token?: string }): Promise<MigrationState> {
    return (await axios.put(`${BASE}/migration/trustvox`, input)).data;
  },
  async completeMigration(): Promise<MigrationState> {
    return (await axios.post(`${BASE}/migration/complete`)).data;
  },
  async getTriggerSuggestion(): Promise<TriggerSuggestion> {
    return (await axios.get(`${BASE}/trigger-suggestion`)).data;
  },
  async getWhatsappLimitCap(): Promise<WhatsappLimitCap | null> {
    return (await axios.get(`${BASE}/whatsapp-limit-cap`)).data.cap;
  },
  async getChecklist(): Promise<Checklist> {
    return (await axios.get(`${BASE}/checklist`)).data;
  },
  async publish(): Promise<{ publishedAt: string }> {
    return (await axios.post(`${BASE}/publish`, { confirmed: true })).data;
  },
  async sendFeedback(score: number, comment: string): Promise<void> {
    await axios.post(`${BASE}/feedback`, { score, comment });
  },
  async uploadLogo(file: File): Promise<OnboardingState> {
    const form = new FormData();
    form.append('file', file);
    return (await axios.post(`${BASE}/logo`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  async requestTeamConnection(): Promise<ConnectionState> {
    return (await axios.post(`${BASE}/connection/request-team`)).data;
  },
  async completeApiStep(): Promise<OnboardingState> {
    return (await axios.post(`${BASE}/connection/api/complete`)).data;
  },
  async getApiIngestion(): Promise<ApiIngestion> {
    return (await axios.get(`${BASE}/api-ingestion`)).data;
  },
  // Retornos de OAuth, com o cliente do caminho
  async completeOAuthInstall(customer: string, body: { state: string; code: string | null; store?: string | null }): Promise<ConnectionState> {
    return (await axios.post(explicitCustomerUrl(customer, `${BASE}/connection/oauth/authorize`), body)).data;
  },
  async completeShopifyInstall(customer: string, params: Record<string, string>): Promise<ConnectionState> {
    return (await axios.post(explicitCustomerUrl(customer, `${BASE}/connection/shopify/authorize`), { params })).data;
  },
  async getShopifyInstallFor(customer: string): Promise<ShopifyInstallParams> {
    return (await axios.get(explicitCustomerUrl(customer, `${BASE}/connection/shopify/install`))).data;
  },
  async getFor(customer: string): Promise<OnboardingState | null> {
    return axios
      // Leitura de passagem (entrada da Shopify): não conta como início do onboarding
      .get(explicitCustomerUrl(customer, BASE), { params: { peek: 'true' } })
      .then((response) => response.data)
      .catch(() => null);
  },
};

export const onboardingStorefrontApi = {
  async get(): Promise<OnboardingStorefrontState> {
    return (await axios.get(`${BASE}/storefront`)).data;
  },
  async checkScript(): Promise<OnboardingStorefrontState> {
    return (await axios.post(`${BASE}/storefront/check-script`)).data;
  },
  async requestHelp(reason: HelpReason, details: string): Promise<OnboardingStorefrontState> {
    return (await axios.post(`${BASE}/storefront/help`, { reason, details })).data;
  },
  async cancelHelp(): Promise<OnboardingStorefrontState> {
    return (await axios.post(`${BASE}/storefront/help/cancel`)).data;
  },
  async approve(): Promise<OnboardingStorefrontState> {
    return (await axios.post(`${BASE}/storefront/approve`)).data;
  },
};

/** Adaptador do painel da loja compartilhado para as rotas do onboarding */
export const onboardingStorefrontPanelApi: StorefrontApi = {
  getState: async () => (await axios.get(`${BASE}/storefront`)).data,
  saveChoices: async (input) => (await axios.put(`${BASE}/storefront`, input)).data,
  installScript: async () => (await axios.post(`${BASE}/storefront/install-script`)).data,
  issueSetupToken: async () => (await axios.post(`${BASE}/storefront/setup-token`)).data,
};

export const onboardingAdminApi = {
  async list(filter?: 'konfidency' | 'open'): Promise<OnboardingQueueItem[]> {
    return (await axios.get(ADMIN_BASE(), { params: filter ? { filter } : {} })).data.onboardings || [];
  },
  async get(key: string): Promise<OnboardingTeamDetail> {
    return (await axios.get(`${ADMIN_BASE()}/${encodeURIComponent(key)}`)).data;
  },
  async getConnection(key: string): Promise<ConnectionState> {
    return (await axios.get(`${ADMIN_BASE()}/${encodeURIComponent(key)}/connection`)).data;
  },
  async markConnectionValid(key: string): Promise<ConnectionState> {
    return (await axios.post(`${ADMIN_BASE()}/${encodeURIComponent(key)}/connection/mark-valid`)).data;
  },
  async getStorefront(key: string): Promise<OnboardingStorefrontState> {
    return (await axios.get(`${ADMIN_BASE()}/${encodeURIComponent(key)}/storefront`)).data;
  },
  async markMigrationDone(key: string): Promise<MigrationState> {
    return (await axios.post(`${ADMIN_BASE()}/${encodeURIComponent(key)}/migration/done`)).data;
  },
  async markImplemented(key: string): Promise<OnboardingStorefrontState> {
    return (await axios.post(`${ADMIN_BASE()}/${encodeURIComponent(key)}/storefront/implemented`)).data;
  },
  async setVtexFeedException(key: string, reason: string): Promise<OnboardingState> {
    return (await axios.put(`${ADMIN_BASE()}/${encodeURIComponent(key)}/vtex-feed-exception`, { reason })).data;
  },
};

const connectionApiWith = (customer: string, url: (path: string) => string): PlatformConnectionApi => ({
  getState: async () => (await axios.get(url('/connection'))).data,
  saveCredentials: async (input) => (await axios.put(url('/connection/credentials'), input)).data,
  saveStoreUrl: async (storeUrl) => (await axios.put(url('/connection/store-url'), { storeUrl })).data,
  validate: async () => (await axios.post(url('/connection/validate'))).data,
  getVtexSalesChannels: async () => (await axios.get(url('/connection/vtex/sales-channels'))).data.channels,
  saveVtexOrderSetup: async (input) => (await axios.put(url('/connection/vtex/order-setup'), input)).data,
  requestShopifyApp: async (shopDomain) => (await axios.post(url('/connection/shopify/request-app'), { shopDomain })).data,
  getShopifyInstall: async () => (await axios.get(url('/connection/shopify/install'))).data,
  shopifyCallbackPath: onboardingShopifyCallbackPath(customer),
  shopifyAppPath: shopifyAppPath(customer),
  getOAuthInstall: async () => (await axios.get(url('/connection/oauth/install'))).data,
  oauthAuthorizeUrl: oauthAuthorizeUrlFor(customer),
  completeTrayManual: async (pasted) => (await axios.post(url('/connection/tray/manual'), { url: pasted })).data,
  getWakeOrderStatuses: async () => (await axios.get(url('/connection/wake/order-statuses'))).data,
  saveWakeOrderSetup: async (wakeStatusMapping) => (await axios.put(url('/connection/wake/order-statuses'), { wakeStatusMapping })).data,
});

/** Adaptador do painel de conexão compartilhado para as rotas do onboarding do cliente ativo */
export const onboardingConnectionApi = (customer: string): PlatformConnectionApi =>
  connectionApiWith(customer, (path) => `${BASE}${path}`);

/** Adaptador da equipe (Asgard): sempre a loja do caminho, com o cadastro do app Shopify */
export const onboardingTeamConnectionApi = (customer: string): PlatformConnectionApi => ({
  ...connectionApiWith(customer, (path) => explicitCustomerUrl(customer, `${BASE}${path}`)),
  getState: async () => onboardingAdminApi.getConnection(customer),
  registerShopifyApp: async (input) => (await axios.put(`${ADMIN_BASE()}/${encodeURIComponent(customer)}/shopify-app`, input)).data,
});
